import * as Sentry from '@sentry/vue';
import type { App } from 'vue';
import type { Router } from 'vue-router';
import {
  shouldDropSentryEventForAxiosBadRequest,
  stripStacktraceFromMissingTranslationEvent,
} from './sentry-before-send.ts';

export type InitSentryOptions = {
  app: App;
  router: Router;
  dsn: string;
  environment: string;
  release: string;
};

/**
 * Initialises the Sentry Vue SDK with browser tracing, session replay, and shared filters.
 */
export function initSentry(options: InitSentryOptions): void {
  const { app, router, dsn, environment, release } = options;

  Sentry.init({
    app,
    dsn,
    environment,
    release,
    ignoreErrors: [
      /Importing a module script failed/,
      /Failed to fetch dynamically imported module/,
      /error loading dynamically imported module/,
      /is not a valid JavaScript MIME type/,
      /Unable to preload CSS/,
    ],
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
      }),
    ],
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpBodies: [],
    },
    tracesSampleRate: 1.0,
    attachStacktrace: true,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      if (shouldDropSentryEventForAxiosBadRequest(hint)) {
        return null;
      }
      return stripStacktraceFromMissingTranslationEvent(event);
    },
  });
}

export const setSentryUser = (id: string, username: string): void => {
  Sentry.setUser({
    id,
    username,
  });
};

export const clearSentryUser = (): void => {
  Sentry.setUser(null);
};

export const setSentryExtra = (key: string, value: string): void => {
  Sentry.setExtra(key, value);
};

export const clearSentryExtra = (key: string): void => {
  Sentry.setExtra(key, undefined);
};
